import type { GuildEventRecord } from "@/lib/guild-events-api"

import { EventStatusBadge } from "@/components/shared/event-status-badge"
import { MetricTile } from "@/components/shared/metric-tile"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import {
  getEventSignupPresentation,
  getSyncStatusLabel,
} from "@/core/guild-events/labels"
import { formatGuildEventDateTimeWithTimezone } from "@/lib/guild-date-format"

import { useGuildEventNow } from "./use-guild-event-now"

function getSourceLabel(
  event: GuildEventRecord,
  scheduleTitle?: string,
): string {
  if (!event.scheduleId) return "Manual"
  return scheduleTitle ? `Schedule: ${scheduleTitle}` : "Schedule unavailable"
}

function getSignupState(event: GuildEventRecord, now: number): string {
  const signup = getEventSignupPresentation(
    {
      eventKind: event.eventKind,
      status: event.status,
      eventRoles: event.eventRoles,
      startAt: new Date(event.startAt),
      signupCloseAt:
        event.eventKind === "raid" ? new Date(event.signupCloseAt) : null,
    },
    new Date(now),
  )
  if (signup.isOpen) {
    return event.eventKind === "raid"
      ? `Open until ${formatGuildEventDateTimeWithTimezone(signup.openUntil)}`
      : `Open until ${formatGuildEventDateTimeWithTimezone(event.startAt)}`
  }
  if (event.status === "started") return "Locked after event start"
  if (event.status === "cancelled")
    return "Unavailable because event is cancelled"
  if (event.status === "announcement_missing")
    return "Unavailable while announcement is recovered"
  return "Closed"
}

export function EventDetailMetadata({
  event,
  scheduleTitle,
}: {
  event: GuildEventRecord | undefined
  scheduleTitle?: string
}) {
  const now = useGuildEventNow()
  const announcementIssue =
    event?.status === "announcement_missing" ||
    event?.syncStatus === "degraded" ||
    (event !== undefined && !event.discordMessageId)

  return (
    <Card>
      <CardContent className="grid gap-4 pt-5">
        <div>
          <h2 className="text-sm font-semibold">Event state</h2>
          <p className="text-sm text-muted-foreground">
            Lifecycle, schedule authority, announcement delivery, and signup
            availability.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <MetricTile label="Lifecycle">
            {event ? <EventStatusBadge status={event.status} /> : "-"}
          </MetricTile>
          <MetricTile
            label="Starts (guild timezone)"
            value={
              event ? formatGuildEventDateTimeWithTimezone(event.startAt) : "-"
            }
          />
          <MetricTile
            label="Source"
            value={event ? getSourceLabel(event, scheduleTitle) : "-"}
          />
          <MetricTile label="Announcement health">
            {event ? (
              <div className="grid gap-1">
                <Badge
                  variant={announcementIssue ? "destructive" : "secondary"}
                >
                  {announcementIssue
                    ? "Recovery needed"
                    : getSyncStatusLabel(event.syncStatus)}
                </Badge>
                <span
                  className={
                    announcementIssue
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {announcementIssue
                    ? "The Discord announcement is missing or degraded."
                    : "Discord announcement is healthy."}
                </span>
              </div>
            ) : (
              "-"
            )}
          </MetricTile>
          {event?.rsvpEnabled ? (
            <MetricTile
              label="Signup state"
              value={getSignupState(event, now)}
            />
          ) : null}
          <MetricTile
            label="Kind"
            value={
              event ? (event.eventKind === "raid" ? "Raid" : "Attendance") : "-"
            }
          />
          {event?.eventKind === "raid" ? (
            <MetricTile
              label="Signup close (guild timezone)"
              value={formatGuildEventDateTimeWithTimezone(event.signupCloseAt)}
            />
          ) : null}
          <MetricTile
            label="Channel"
            value={event?.channelName ?? event?.channelId ?? "-"}
            className="break-all"
          />
          <MetricTile
            label="Created"
            value={
              event
                ? formatGuildEventDateTimeWithTimezone(event.createdAt)
                : "-"
            }
          />
          <MetricTile
            label="Updated"
            value={
              event
                ? formatGuildEventDateTimeWithTimezone(event.updatedAt)
                : "-"
            }
          />
        </div>
      </CardContent>
    </Card>
  )
}
